// apps/admin/src/components/AggregatorLabel.tsx
// Displays an aggregator name as a colored chip/badge.
// Used in OnboardingDataUploader to show which aggregators support each upload method.

import React from 'react';

/**
 * Aggregator names as they should be displayed in the UI.
 * Uppercase for visual consistency as "label chips".
 */
const AGGREGATOR_DISPLAY_NAMES = {
  AFG: 'AFG',
  ENTORS: 'ENTORS',
  LOAN_MARKET: 'LOAN MARKET',
  ENTICAL: 'ENTICAL',
  ENTOVER: 'ENTOVER',
  ENTOSSEY: 'ENTOSSEY',
  WLTH: 'WLTH',
  ORDE: 'ORDE',
  ASTUTE: 'ASTUTE',
  CONNECTIVE: 'CONNECTIVE',
  // 2026-04-30: new aggregators
  VISION: 'VISION',
  CHOICE: 'CHOICE',
  LMG: 'LMG',
} as const;

export type AggregatorName = keyof typeof AGGREGATOR_DISPLAY_NAMES;

/**
 * Color configuration for aggregator labels.
 * Uses existing design system colors from the admin app.
 */
const AGGREGATOR_COLORS: Record<AggregatorName, { bg: string; text: string; border: string }> = {
  AFG: {
    bg: 'bg-blue-500/20',
    text: 'text-blue-400',
    border: 'border-blue-500/30',
  },
  ENTORS: {
    bg: 'bg-purple-500/20',
    text: 'text-purple-400',
    border: 'border-purple-500/30',
  },
  LOAN_MARKET: {
    bg: 'bg-emerald-500/20',
    text: 'text-emerald-400',
    border: 'border-emerald-500/30',
  },
  ENTICAL: {
    bg: 'bg-amber-500/20',
    text: 'text-amber-400',
    border: 'border-amber-500/30',
  },
  ENTOVER: {
    bg: 'bg-rose-500/20',
    text: 'text-rose-400',
    border: 'border-rose-500/30',
  },
  ENTOSSEY: {
    bg: 'bg-cyan-500/20',
    text: 'text-cyan-400',
    border: 'border-cyan-500/30',
  },
  WLTH: {
    bg: 'bg-orange-500/20',
    text: 'text-orange-400',
    border: 'border-orange-500/30',
  },
  ORDE: {
    bg: 'bg-pink-500/20',
    text: 'text-pink-400',
    border: 'border-pink-500/30',
  },
  ASTUTE: {
    bg: 'bg-teal-500/20',
    text: 'text-teal-400',
    border: 'border-teal-500/30',
  },
  CONNECTIVE: {
    bg: 'bg-indigo-500/20',
    text: 'text-indigo-400',
    border: 'border-indigo-500/30',
  },
  // 2026-04-30: new aggregators
  VISION: {
    bg: 'bg-violet-500/20',
    text: 'text-violet-400',
    border: 'border-violet-500/30',
  },
  CHOICE: {
    bg: 'bg-fuchsia-500/20',
    text: 'text-fuchsia-400',
    border: 'border-fuchsia-500/30',
  },
  LMG: {
    bg: 'bg-lime-500/20',
    text: 'text-lime-400',
    border: 'border-lime-500/30',
  },
};

interface AggregatorLabelProps {
  aggregator: AggregatorName;
  size?: 'sm' | 'md';
}

/**
 * Displays an aggregator name as a colored chip/badge.
 * Follows existing design patterns from TemplatesPage.tsx status badges.
 */
export const AggregatorLabel: React.FC<AggregatorLabelProps> = ({
  aggregator,
  size = 'sm',
}) => {
  const colors = AGGREGATOR_COLORS[aggregator];
  const displayName = AGGREGATOR_DISPLAY_NAMES[aggregator];

  const sizeClasses =
    size === 'sm'
      ? 'px-1.5 py-0.5 text-[10px]'
      : 'px-2 py-1 text-xs';

  return (
    <span
      className={`
        inline-flex items-center rounded border font-medium
        ${colors.bg} ${colors.text} ${colors.border}
        ${sizeClasses}
      `}
    >
      {displayName}
    </span>
  );
};

export default AggregatorLabel;
